import { BadgeCheck, Leaf, Flame, Hourglass } from "lucide-react";
import { MaskReveal, Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { Marquee } from "../components/Marquee";
import { Seo } from "../components/Seo";
import { ABOUT_IMAGE, GALLERY } from "../data/siteData";

const VALUES = [
  {
    icon: BadgeCheck,
    title: "Certified Therapists",
    copy: "Every therapist is trained in multiple disciplines and re-certified each year.",
  },
  {
    icon: Leaf,
    title: "Botanical Oils",
    copy: "Cold-pressed, blended in-house, and matched to your body on the day.",
  },
  {
    icon: Flame,
    title: "Private Suites",
    copy: "Candlelit, sound-softened rooms that are yours alone for the full session.",
  },
  {
    icon: Hourglass,
    title: "Unhurried Sessions",
    copy: "Sessions run 45 to 120 minutes — and never feel like the clock is watching.",
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Us | Belong Massage-Only Spa in Suchitra, Hyderabad"
        description="The story of Belong Salon & Spa — a massage-only luxury spa in Suchitra, Hyderabad. Certified therapists, botanical oils, private candlelit suites and unhurried sessions."
        keywords="about Belong spa, massage-only spa Hyderabad, luxury spa Suchitra, certified massage therapists Hyderabad"
      />
      <section className="relative overflow-hidden pb-20 pt-44" data-testid="about-hero">
        <div className="ambient-glow pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <MaskReveal delay={0.15}>
            <p className="font-mono-alt text-[11px] uppercase tracking-[0.35em] text-amber-400/80">About Belong</p>
          </MaskReveal>
          <h1 className="mt-8 max-w-4xl font-display text-4xl font-light leading-[1.06] tracking-tight text-amber-50 sm:text-6xl lg:text-7xl">
            <MaskReveal delay={0.3}>A sanctuary built around</MaskReveal>
            <MaskReveal delay={0.45}>
              <em className="text-gold-gradient not-italic">one belief.</em>
            </MaskReveal>
          </h1>
          <MaskReveal delay={0.7} className="mt-10 max-w-2xl">
            <p className="text-base font-light leading-relaxed text-stone-400 sm:text-lg">
              That the body keeps score — of deadlines, traffic, screens, silence — and that skilled,
              unhurried touch is how the ledger gets cleared.
            </p>
          </MaskReveal>
        </div>
      </section>

      <section className="py-20" data-testid="about-story">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <div className="spotlight-frame overflow-hidden rounded-3xl border border-amber-500/20">
              <img
                src={ABOUT_IMAGE}
                alt="Inside Belong's reception lounge"
                className="aspect-[16/10] w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-mono-alt text-[11px] uppercase tracking-[0.3em] text-amber-400/80">Our Story</p>
            <h2 className="mt-6 font-display text-3xl font-light leading-[1.12] tracking-tight text-amber-50 sm:text-4xl">
              Massage-only, <em className="text-gold-gradient not-italic">by design.</em>
            </h2>
            <p className="mt-8 text-base font-light leading-relaxed text-stone-400">
              Belong opened its doors above the bustle of Suchitra with a deliberate omission: no
              salon chairs, no styling mirrors, no rush of a multi-service floor. Just massage
              suites, warm oil, and time.
            </p>
            <p className="mt-5 text-base font-light leading-relaxed text-stone-400">
              That focus let us go deeper — better training for our therapists, better oils, longer
              sessions, quieter rooms. Seven services, refined until each one earns its place on the
              menu.
            </p>
            <p className="mt-5 text-base font-light leading-relaxed text-stone-400">
              Guests come from across Hyderabad for an hour of stillness. They leave with something
              rarer: a body that feels like home again.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-amber-500/10 bg-[#0D0D0F] py-24" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="font-mono-alt text-[11px] uppercase tracking-[0.3em] text-amber-400/80">What We Hold Dear</p>
            <h2 className="mt-5 font-display text-3xl font-light tracking-tight text-amber-50 sm:text-4xl">
              The quiet standards
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div
                  className="group h-full rounded-2xl border border-white/8 bg-[#121215] p-8 transition-all duration-500 hover:border-amber-500/30 hover:shadow-[0_0_30px_rgba(212,175,55,0.08)]"
                  data-testid={`value-card-${i}`}
                >
                  <v.icon className="h-6 w-6 text-amber-400/80" strokeWidth={1.5} />
                  <h3 className="mt-6 font-display text-xl font-normal text-amber-100">{v.title}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-stone-400">{v.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="about-gallery">
        <div className="mx-auto grid max-w-7xl gap-5 px-6 sm:grid-cols-2 lg:px-10">
          <Reveal>
            <div className="overflow-hidden rounded-2xl border border-amber-500/15">
              <img
                src={GALLERY[1]}
                alt="Spa service in progress"
                className="aspect-[4/3] w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="overflow-hidden rounded-t-full border border-amber-500/15">
              <img
                src={GALLERY[2]}
                alt="Relaxation service ambience"
                className="aspect-[4/3] w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee />
      <CTASection />
    </>
  );
}
